import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from '../config';

interface RoadmapItem {
  _id: string;
  type: 'todo' | 'bug';
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'completed';
  createdAt: string;
  completedAt: string | null;
}

const RoadmapBugs: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${config.api.baseURL}/api/roadmap`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Sort items by createdAt date
        const sortedItems = response.data.sort((a: RoadmapItem, b: RoadmapItem) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setItems(sortedItems);
      } catch (error) {
        console.error('Error fetching roadmap items:', error);
        setError('Failed to load roadmap items');
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [getAccessTokenSilently]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  const todoItems = items.filter(item => item.type === 'todo');
  const bugItems = items.filter(item => item.type === 'bug');

  const getRowStyle = (status: string) => ({
    opacity: status === 'completed' ? 0.6 : 1,
    backgroundColor: status === 'completed' ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
  });

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Roadmap & Known Bugs
        </Typography>

        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Todo List
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell>Completed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {todoItems.map(item => (
                  <TableRow key={item._id} sx={getRowStyle(item.status)}>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.status}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {item.completedAt ? new Date(item.completedAt).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {todoItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No todo items - Check back later for updates
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Known Bugs
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell>Completed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bugItems.map(item => (
                  <TableRow key={item._id} sx={getRowStyle(item.status)}>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.status}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {item.completedAt ? new Date(item.completedAt).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {bugItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No known bugs - Please report any issues you find
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default RoadmapBugs; 